import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Card, 
  CardContent, 
  CardActions,
  Chip,
  IconButton,
  MenuItem,
  Divider,
  InputAdornment,
  Rating,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  CloudUpload as UploadIcon, 
  Download as DownloadIcon, 
  Search as SearchIcon, 
  Delete as DeleteIcon,
  Folder as FolderIcon,
  Edit as EditIcon,
  Category as CategoryIcon,
  Add as AddIcon
} from '@mui/icons-material';
import api from '../services/api';
import { TiptapEditor } from '../components/TiptapEditor';

const ResourceCenter = () => {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [openCat, setOpenCat] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: ''
  });

  // Category management state
  const [catFormData, setCatFormData] = useState({ id: null, name: '', description: '' });
  const [isEditingCat, setIsEditingCat] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchData = async () => {
    try {
      const rRes = await api.get('/resources');
      setResources(rRes.data);
      const cRes = await api.get('/resources/categories');
      setCategories(cRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpen = (resource: any = null) => {
    if (resource) {
      setEditingId(resource.id);
      setFormData({
        title: resource.title,
        description: resource.description,
        category_id: resource.category_id
      });
    } else {
      setEditingId(null);
      setFormData({ title: '', description: '', category_id: '' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    setFile(null);
    setFormData({ title: '', description: '', category_id: '' });
  };

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.category_id || !formData.title) return;

    try {
      if (editingId) {
        // Edit existing
        await api.put(`/resources/${editingId}`, formData);
      } else {
        // Upload new
        if (!file) return;
        const data = new FormData();
        data.append('file', file);
        data.append('title', formData.title);
        data.append('description', formData.description);
        data.append('category_id', formData.category_id);
        await api.post('/resources/upload', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      handleClose();
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownload = async (id: string, url: string) => {
    try {
      await api.put(`/resources/${id}/download`);
      window.open(url, '_blank');
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRate = async (resourceId: string, rating: number | null) => {
    if (!rating) return;
    try {
      await api.post('/resources/rate', { resourceId, rating });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('ยืนยันการลบสื่อการสอนนี้?')) {
      try {
        await api.delete(`/resources/${id}`);
        fetchData();
      } catch (err) {
        console.error(err);
      }
    }
  };

  // Category Handlers
  const handleSaveCategory = async () => {
    try {
      if (isEditingCat) {
        await api.put(`/resources/categories/${catFormData.id}`, catFormData);
      } else {
        await api.post('/resources/categories', catFormData);
      }
      setCatFormData({ id: null, name: '', description: '' });
      setIsEditingCat(false);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (window.confirm('ยืนยันการลบหมวดหมู่?')) {
      try {
        await api.delete(`/resources/categories/${id}`);
        fetchData();
      } catch (err: any) {
        alert(err.response?.data?.message || 'ไม่สามารถลบได้');
      }
    }
  };

  const filteredResources = resources.filter((r: any) => 
    r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.category_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 2, 
        mb: 3 
      }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          <FolderIcon sx={{ mr: 1, color: 'primary.main' }} /> คลังสื่อการสอนและทรัพยากร (Resource Center)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          {user.role === 'ADMIN' && (
            <Button 
              variant="outlined" 
              color="secondary"
              startIcon={<CategoryIcon />} 
              onClick={() => setOpenCat(true)}
            >
              จัดการหมวดหมู่
            </Button>
          )}
          <Button 
            variant="contained" 
            startIcon={<UploadIcon />} 
            onClick={() => handleOpen()}
            sx={{ flexGrow: { xs: 1, sm: 0 } }}
          >
            แบ่งปันสื่อใหม่
          </Button>
        </Box>
      </Box>

      <TextField
        fullWidth
        placeholder="ค้นหาชื่อสื่อ, หัวข้อ, หรือประเภท..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 4 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />

      <Grid container spacing={3}>
        {filteredResources.map((r: any) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={r.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 2 }}>
              <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Chip 
                    label={r.category_name} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                    sx={{ mb: 1.5 }} 
                  />
                  <Box>
                    {(user.id === r.uploader_user_id || user.role === 'ADMIN') && (
                      <>
                        <IconButton size="small" color="primary" onClick={() => handleOpen(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>{r.title}</Typography>
                <Box
                  sx={{
                    mb: 2,
                    minHeight: 40,
                    maxHeight: 70,
                    overflow: 'hidden',
                    fontSize: '0.85rem',
                    color: 'text.secondary',
                    '& p': { m: 0 },
                  }}
                  dangerouslySetInnerHTML={{ __html: r.description || 'ไม่มีคำอธิบาย' }}
                />
                
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating 
                    value={parseFloat(r.avg_rating || 0)} 
                    precision={0.5} 
                    onChange={(e, val) => handleRate(r.id, val)}
                    size="small" 
                  />
                  <Typography variant="caption" color="textSecondary">
                    ({r.rating_count || 0})
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                  โดย: {r.uploader_name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  แชร์เมื่อ: {new Date(r.created_at).toLocaleDateString('th-TH')}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                  ดาวน์โหลดแล้ว {r.download_count} ครั้ง
                </Typography>
                <Button 
                  size="small" 
                  variant="contained" 
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(r.id, `/uploads/${r.file_path.split('/').pop()}`)}
                >
                  ดาวน์โหลด
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
        {filteredResources.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">ไม่พบสื่อการสอนที่ตรงตามเงื่อนไข</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Resource Dialog (Upload/Edit) */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'แก้ไขรายละเอียดสื่อ' : 'แบ่งปันสื่อการสอน (Share Resource)'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="ชื่อหัวข้อสื่อ"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                label="หมวดหมู่"
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                required
              >
                {categories.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TiptapEditor
                label="รายละเอียด / วิธีการใช้งานสื่อ (Rich Text)"
                value={formData.description}
                onChange={(html) => setFormData((prev) => ({ ...prev, description: html }))}
                placeholder="ระบุวัตถุประสงค์ วิธีนำไปใช้ หรือคำแนะนำประกอบสื่อ..."
                minHeight={150}
              />
            </Grid>
            {!editingId && (
              <Grid size={{ xs: 12 }}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ py: 2, borderStyle: 'dashed' }}
                >
                  {file ? `ไฟล์ที่เลือก: ${file.name}` : 'คลิกเพื่อเลือกไฟล์ (PDF, PPT, Doc, Zip)'}
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)}
                  />
                </Button>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>ยกเลิก</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={(!editingId && !file) || !formData.category_id || !formData.title}
          >
            {editingId ? 'บันทึกการแก้ไข' : 'อัปโหลดและแบ่งปัน'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={openCat} onClose={() => setOpenCat(false)} fullWidth maxWidth="md">
        <DialogTitle>จัดการหมวดหมู่สื่อการสอน</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField 
                  fullWidth 
                  size="small" 
                  label="ชื่อหมวดหมู่" 
                  value={catFormData.name}
                  onChange={(e) => setCatFormData({...catFormData, name: e.target.value})}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField 
                  fullWidth 
                  size="small" 
                  label="คำอธิบาย" 
                  value={catFormData.description}
                  onChange={(e) => setCatFormData({...catFormData, description: e.target.value})}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  startIcon={isEditingCat ? <EditIcon /> : <AddIcon />}
                  onClick={handleSaveCategory}
                  disabled={!catFormData.name}
                >
                  {isEditingCat ? 'แก้ไข' : 'เพิ่ม'}
                </Button>
              </Grid>
            </Grid>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell>ชื่อหมวดหมู่</TableCell>
                  <TableCell>คำอธิบาย</TableCell>
                  <TableCell align="right">การจัดการ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((c: any) => (
                  <TableRow key={c.id}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{c.name}</TableCell>
                    <TableCell>{c.description}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => { 
                        setCatFormData(c); 
                        setIsEditingCat(true); 
                      }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteCategory(c.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCat(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResourceCenter;
